import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { registerAccount } from '../services/authApi';
import './Pages.css';

export const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    crn: '',
    phone: '',
    role: 'nutritionist' as 'patient' | 'nutritionist',
    nutritionistEmail: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    const name = e.target.name;
    
    setFormData({
      ...formData,
      [name]: value,
    });
    setError(null);
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Por favor, insira um email válido');
      return false;
    }
    // Se for paciente e informou email do nutricionista, validar
    if (formData.role === 'patient' && formData.nutritionistEmail.trim()) {
      if (!formData.nutritionistEmail.includes('@')) {
        setError('Por favor, insira um email válido do nutricionista');
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.password || !formData.confirmPassword) {
      setError('Por favor, preencha todos os campos de senha');
      return false;
    }
    if (formData.password.length < 10) {
      setError('A senha deve ter pelo menos 10 caracteres');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      setError(null);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateStep2()) return;

    setLoading(true);
    try {
      const result = await registerAccount({
        email: formData.email.trim(),
        password: formData.password,
        name: formData.name.trim(),
        role: formData.role,
        crn: formData.role === 'nutritionist' ? formData.crn.trim() : undefined,
        phone: formData.phone.trim() || undefined,
        nutritionistEmail: formData.role === 'patient' ? formData.nutritionistEmail.trim() || undefined : undefined,
      });
      navigate('/login', {
        replace: true,
        state: { registrationPending: true, verificationEmailSent: result.emailSent !== false },
      });
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ns-signup">
      <div className="ns-signup-container">
        <div className="ns-signup-header">
          <Link to="/" className="ns-signup-logo-link">
            <div className="ns-signup-logo">
              <span className="mark">NS</span>
              <span className="text">NutriSync</span>
            </div>
          </Link>
          {formData.role === 'nutritionist' && (
            <div className="ns-signup-badge">
              <span>✨</span> Teste Grátis por 7 dias
            </div>
          )}
        </div>

        <div className="ns-signup-content">
          <div className="ns-signup-left">
            <h1 className="ns-signup-title">
              Comece sua jornada digital
              <span className="ns-signup-gradient"> hoje mesmo</span>
            </h1>
            <p className="ns-signup-subtitle">
              Crie sua conta e tenha acesso completo a todas as funcionalidades por 7 dias, sem compromisso.
            </p>

            <div className="ns-signup-benefits">
              <div className="ns-signup-benefit-item">
                <div className="ns-signup-benefit-icon">✓</div>
                <div>
                  <strong>Acesso completo</strong>
                  <span>Todas as funcionalidades disponíveis</span>
                </div>
              </div>
              <div className="ns-signup-benefit-item">
                <div className="ns-signup-benefit-icon">✓</div>
                <div>
                  <strong>Sem cartão de crédito</strong>
                  <span>Comece agora, sem compromisso</span>
                </div>
              </div>
              <div className="ns-signup-benefit-item">
                <div className="ns-signup-benefit-icon">✓</div>
                <div>
                  <strong>Suporte completo</strong>
                  <span>Equipe disponível durante todo o teste</span>
                </div>
              </div>
            </div>
          </div>

          <div className="ns-signup-right">
            <div className="ns-signup-panel">
              <div className="ns-signup-panel-header">
                <h2>Criar conta</h2>
                <p>Preencha os dados abaixo para começar</p>
              </div>

              {step === 1 && (
                <form className="ns-signup-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                  <div className="ns-signup-step-indicator">
                    <span className="ns-signup-step-active">1</span>
                    <div className="ns-signup-step-line"></div>
                    <span className="ns-signup-step">2</span>
                  </div>

                  <label className="ns-field">
                    <span>Tipo de Conta *</span>
                    <select
                      className="ns-input"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="nutritionist">Nutricionista</option>
                      <option value="patient">Paciente</option>
                    </select>
                  </label>

                  <label className="ns-field">
                    <span>Nome completo *</span>
                    <input
                      className="ns-input"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={formData.role === 'nutritionist' ? 'Ex: Dr. Maria Silva' : 'Ex: João Silva'}
                      required
                    />
                  </label>

                  <label className="ns-field">
                    <span>Email {formData.role === 'nutritionist' ? 'profissional' : ''} *</span>
                    <input
                      className="ns-input"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={formData.role === 'nutritionist' ? 'seuemail@clinica.com' : 'seuemail@exemplo.com'}
                      required
                    />
                  </label>

                  {formData.role === 'patient' && (
                    <label className="ns-field">
                      <span>Email do Nutricionista (para vínculo)</span>
                      <input
                        className="ns-input"
                        type="email"
                        name="nutritionistEmail"
                        value={formData.nutritionistEmail}
                        onChange={handleChange}
                        placeholder="ex.: daniel@clinica.com"
                      />
                    </label>
                  )}

                  {formData.role === 'nutritionist' && (
                    <>
                      <label className="ns-field">
                        <span>CRN (opcional)</span>
                        <input
                          className="ns-input"
                          type="text"
                          name="crn"
                          value={formData.crn}
                          onChange={handleChange}
                          placeholder="Ex: CRN-3 12345"
                        />
                      </label>

                      <label className="ns-field">
                        <span>Telefone (opcional)</span>
                        <input
                          className="ns-input"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(11) 99999-9999"
                        />
                      </label>
                    </>
                  )}

                  {error && <p className="ns-signup-error">{error}</p>}

                  <div className="ns-signup-form-actions">
                    <Button type="submit" className="ns-signup-btn-primary">
                      Continuar
                    </Button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <form className="ns-signup-form" onSubmit={handleSubmit}>
                  <div className="ns-signup-step-indicator">
                    <span className="ns-signup-step-completed">✓</span>
                    <div className="ns-signup-step-line ns-signup-step-line-active"></div>
                    <span className="ns-signup-step-active">2</span>
                  </div>

                  <div className="ns-signup-review">
                    <p className="ns-signup-review-title">Confirme seus dados:</p>
                    <div className="ns-signup-review-item">
                      <span>Tipo de Conta:</span>
                      <strong>{formData.role === 'nutritionist' ? 'Nutricionista' : 'Paciente'}</strong>
                    </div>
                    <div className="ns-signup-review-item">
                      <span>Nome:</span>
                      <strong>{formData.name}</strong>
                    </div>
                    <div className="ns-signup-review-item">
                      <span>Email:</span>
                      <strong>{formData.email}</strong>
                    </div>
                    {formData.role === 'nutritionist' && formData.crn && (
                      <div className="ns-signup-review-item">
                        <span>CRN:</span>
                        <strong>{formData.crn}</strong>
                      </div>
                    )}
                    {formData.role === 'patient' && formData.nutritionistEmail && (
                      <div className="ns-signup-review-item">
                        <span>Nutricionista:</span>
                        <strong>{formData.nutritionistEmail}</strong>
                      </div>
                    )}
                  </div>

                  <label className="ns-field">
                    <span>Senha *</span>
                    <input
                      className="ns-input"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                  </label>

                  <label className="ns-field">
                    <span>Confirmar senha *</span>
                    <input
                      className="ns-input"
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Digite a senha novamente"
                      required
                    />
                  </label>

                  {error && <p className="ns-signup-error">{error}</p>}

                  <div className="ns-signup-form-actions">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleBack}
                      disabled={loading}
                    >
                      Voltar
                    </Button>
                    <Button type="submit" disabled={loading} className="ns-signup-btn-primary">
                      {loading ? 'Criando conta...' : 'Criar conta e começar'}
                    </Button>
                  </div>
                </form>
              )}

              <div className="ns-signup-footer">
                <p>
                  Já tem uma conta? <Link to="/login">Fazer login</Link>
                </p>
                <p className="ns-signup-terms">
                  Ao criar uma conta, você concorda com nossos{' '}
                  <Link to="/termos-de-uso">Termos de Uso</Link> e{' '}
                  <Link to="/privacidade">Política de Privacidade</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
